import { useCallback, useMemo, useState } from 'react'

import { useNotification } from 'app/providers'
import { Button } from 'common/components/Button/Button'
import { Input } from 'common/components/Input/Input'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { BUTTON_MODIFIER, BUTTON_PROPORTION } from 'common/constants/buttonConstants'
import { formatMoney } from 'common/services/format'
import type { EncounterCheckoutCreditCard } from 'features/Procedure/state/procedure-api.type'
import { SettingsService } from 'features/SettingsOld/services/settingsService'

import { CcOnFileRow, type CcOnFileValues } from './cc-on-file-row'
import { parseExpiration, useStripeCard, type ValidatedCard } from './use-stripe-card'

import styles from './credit-card-group.module.scss'

const { formatNumber, formatExpiryDate, formatCVC } = SettingsService

export type CreditCardPayment = ValidatedCard & {
  amount: number
  cardholder: string
}

type Props = {
  onFile: EncounterCheckoutCreditCard | null | undefined
  ccValues: CcOnFileValues
  onCcChange: (next: CcOnFileValues) => void
  onSaveCc: (values: CcOnFileValues) => Promise<{ success: boolean; error?: string }>
  maxAmount: number
  validated: CreditCardPayment[]
  onAddValidated: (card: CreditCardPayment) => void
  onRemoveValidated: (token: string) => void
  disabled?: boolean
  stripeKey: string
}

const parseAmount = (raw: string, max: number): number => {
  const n = Number.parseFloat(raw)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.min(n, max)
}

export const CreditCardGroup = ({
  onFile,
  ccValues,
  onCcChange,
  onSaveCc,
  maxAmount,
  validated,
  onAddValidated,
  onRemoveValidated,
  disabled,
  stripeKey,
}: Props) => {
  const [amountInput, setAmountInput] = useState('')
  const [cardNumberInput, setCardNumberInput] = useState('')
  const [cardExpInput, setCardExpInput] = useState('')
  const [cardCvcInput, setCardCvcInput] = useState('')
  const [cardZipInput, setCardZipInput] = useState('')
  const { errorMessage, isValidating, validate, reset } = useStripeCard(stripeKey)
  const { setNotification } = useNotification()

  const handleValidate = useCallback(async () => {
    const amount = parseAmount(amountInput, maxAmount)
    if (amount <= 0) {
      setNotification({
        type: ALERT_CONSTANTS.WARNING,
        title: 'Validation',
        description: 'Please enter an amount',
      })
      return
    }
    const cardholder = ccValues.cc_name.trim()
    if (!cardholder) {
      setNotification({
        type: ALERT_CONSTANTS.WARNING,
        title: 'Validation',
        description: 'Please enter cardholder name',
      })
      return
    }
    const exp = parseExpiration(cardExpInput)
    if (!exp) {
      setNotification({
        type: ALERT_CONSTANTS.WARNING,
        title: 'Validation',
        description: 'Invalid expiration date',
      })
      return
    }
    const number = cardNumberInput.replace(/\s+/g, '')
    if (!number) {
      setNotification({
        type: ALERT_CONSTANTS.WARNING,
        title: 'Validation',
        description: 'Please enter card number',
      })
      return
    }
    const cvc = cardCvcInput.trim()
    if (!cvc) {
      setNotification({
        type: ALERT_CONSTANTS.WARNING,
        title: 'Validation',
        description: 'Please enter CVV',
      })
      return
    }
    const zip = cardZipInput.trim() || ccValues.cc_zip.trim()
    const result = await validate({
      number,
      expMonth: exp.month,
      expYear: exp.year,
      cvc,
      name: cardholder,
      addressZip: zip || undefined,
    })
    if (result.ok === false) {
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        title: 'Card validation failed',
        description: result.error,
      })
      return
    }
    const card = result.card
    onAddValidated({ ...card, amount, cardholder })
    setNotification({
      type: ALERT_CONSTANTS.SUCCESS,
      title: 'Card validated',
      description: `${card.brand?.toUpperCase() || 'Card'} ****${card.last4} added`,
      duration: 3,
    })
    setAmountInput('')
    setCardNumberInput('')
    setCardExpInput('')
    setCardCvcInput('')
    setCardZipInput('')
    reset()
  }, [
    amountInput,
    cardCvcInput,
    cardExpInput,
    cardNumberInput,
    cardZipInput,
    ccValues.cc_name,
    ccValues.cc_zip,
    maxAmount,
    onAddValidated,
    reset,
    setNotification,
    validate,
  ])

  const validatedTotal = useMemo(() => validated.reduce((sum, c) => sum + c.amount, 0), [validated])

  return (
    <div className={styles.group}>
      {validated.length > 0 && (
        <div className={styles.validatedList}>
          {validated.map((card) => (
            <div key={card.token} className={styles.validatedItem}>
              <span>
                <span className={styles.brand}>{card.brand}</span> ****{card.last4} (
                {card.cardholder})
              </span>
              <span>{formatMoney(card.amount)}</span>
              <button
                type='button'
                className={styles.removeBtn}
                onClick={() => onRemoveValidated(card.token)}
                disabled={disabled}>
                Remove
              </button>
            </div>
          ))}
          <div className={styles.validatedTotalRow}>
            <strong>Validated total</strong>
            <strong>{formatMoney(validatedTotal)}</strong>
          </div>
        </div>
      )}

      <div className={styles.amountRow}>
        <Input
          name='cc_amount'
          label='Amount'
          placeholder='0.00'
          inputMode='decimal'
          value={amountInput}
          parentClassName={styles.amountField}
          disabled={disabled}
          onChange={(event) => setAmountInput(event.target.value)}
        />
      </div>

      <CcOnFileRow
        initial={onFile}
        values={ccValues}
        onChange={onCcChange}
        onSave={onSaveCc}
        disabled={disabled}
      />

      <div className={styles.cardRow}>
        <Input
          name='cc_card_number'
          label='Card Number'
          placeholder='1234 5678 9012 3456'
          value={cardNumberInput}
          parentClassName={styles.cardNumberField}
          disabled={disabled}
          onChange={(event) => setCardNumberInput(formatNumber(event.target.value))}
        />
        <Input
          name='cc_card_exp'
          label='MM/YY'
          placeholder='MM/YY'
          value={cardExpInput}
          parentClassName={styles.cardExpField}
          disabled={disabled}
          onChange={(event) => setCardExpInput(formatExpiryDate(event.target.value))}
        />
        <Input
          name='cc_card_cvv'
          label='CVV'
          placeholder='123'
          value={cardCvcInput}
          parentClassName={styles.cardCvcField}
          disabled={disabled}
          onChange={(event) => setCardCvcInput(formatCVC(event.target.value))}
        />
        <Input
          name='cc_card_zip'
          label='ZIP'
          placeholder='12345'
          value={cardZipInput}
          parentClassName={styles.cardZipField}
          disabled={disabled}
          onChange={(event) => setCardZipInput(event.target.value)}
        />
        <Button
          modifier={BUTTON_MODIFIER.PRIMARY}
          proportion={BUTTON_PROPORTION.SMALL}
          disabled={disabled || isValidating}
          onClick={handleValidate}>
          {isValidating ? 'Validating...' : 'Validate Card'}
        </Button>
      </div>

      {errorMessage && <div className={styles.cardError}>{errorMessage}</div>}
    </div>
  )
}
